import cn from 'classnames'
import React from 'react'
import {
   ToastContainer as Container,
   ToastContainerProps
} from 'react-toastify'

import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'
import { XCloseIcon } from '@flowwai-frontend/design-system/icons'
import { Button, Hierarchy, SubVariant } from '@flowwai-frontend/design-system'

import {
   LinkType,
   ToastActionType,
   ToastStyles,
   ToastType,
   ToastVariant
} from '../../types/ToastTypes'

import {
   actionsContainerClassName,
   buttonClassName,
   closeContainerStyles,
   containerClassName,
   contentContainerClassName,
   getToastStyles,
   leftIconClassName,
   toastDescriptionStyles,
   toastTitleStyles,
   inlineVariantContainerStyles,
   inlineVariantTitleWithAdditionalInfoStyles
} from './styles'

import './index.css'

interface ToastComponentProps {
   title: string
   toastType: ToastType
   dismissToast?: () => void
   color?: string

   description?: string

   renderLeftIcon?: (color: string) => React.ReactElement

   actions?: ToastActionType[]

   noteList?: string[]

   rightLink?: LinkType
   showDismissButton?: boolean

   toastClassName?: string
   uiVariant?: ToastVariant
}

const ToastComponent = (props: ToastComponentProps): React.ReactElement => {
   const { theme } = useAppTheme()

   const toastTheme = theme.toast

   const {
      title,
      description,
      toastType = 'SUCCESS',
      showDismissButton = true,
      rightLink,
      noteList = [],
      actions = [],
      dismissToast,
      renderLeftIcon,
      toastClassName,
      uiVariant = 'STACKED'
   } = props

   const {
      bgColor,
      borderColor,
      textColor,
      closeIconColor,
      leftIconColor,
      buttonTextColor
   }: ToastStyles = getToastStyles(toastTheme, toastType)

   const renderToastActions = (): React.ReactNode =>
      actions.length ? (
         <div className={actionsContainerClassName}>
            {actions.map(action => (
               <Button
                  hierarchy={action.hierarchy || Hierarchy.Link}
                  subVariant={action.subVariant || SubVariant.Gray}
                  size={'Small'}
                  className={cn(
                     buttonClassName,
                     toastDescriptionStyles,
                     buttonTextColor,
                     action.containerClassName
                  )}
                  key={action.value}
                  onClick={action.onClickAction}
                  leftIcon={action.renderLeftIcon}
                  rightIcon={action.renderRightIcon}
               >
                  {action.label}
               </Button>
            ))}
         </div>
      ) : null

   const renderToastNoteList = (): React.ReactNode =>
      noteList.length ? (
         <ul className={'list-disc ml-18px'}>
            {noteList.map((note, index) => (
               <li
                  className={cn(toastDescriptionStyles, textColor)}
                  key={index}
               >
                  {note}
               </li>
            ))}
         </ul>
      ) : null

   const renderToastDescription = (): React.ReactNode =>
      description ? (
         <span className={cn(toastDescriptionStyles, textColor)}>
            {description}
         </span>
      ) : null

   const renderAdditionalInfo = () => (
      //TODO: update button styles
      <div>
         {renderToastDescription()}
         {renderToastNoteList()}
         {renderToastActions()}
      </div>
   )

   const renderToastTitle = (): React.ReactElement => (
      <span className={cn(toastTitleStyles, textColor)}>{title}</span>
   )

   const renderInlineVariantToastContent = (): React.ReactElement => (
      <div className={cn(inlineVariantContainerStyles)}>
         <div className={cn(inlineVariantTitleWithAdditionalInfoStyles)}>
            {renderToastTitle()}
            {renderToastDescription()}
            {renderToastNoteList()}
         </div>
         {renderToastActions()}
      </div>
   )

   const renderStackedVariantToastContent = (): React.ReactElement => (
      <div className={contentContainerClassName}>
         {renderToastTitle()}
         {renderAdditionalInfo()}
      </div>
   )

   const renderToastContentBasedOnVariant = (): React.ReactElement => {
      switch (uiVariant) {
         case 'INLINE': {
            return renderInlineVariantToastContent()
         }

         case 'STACKED':
         default: {
            return renderStackedVariantToastContent()
         }
      }
   }

   return (
      <div
         className={cn(
            containerClassName,
            bgColor,
            borderColor,
            toastClassName
         )}
      >
         <div className={leftIconClassName}>
            {renderLeftIcon ? renderLeftIcon(leftIconColor) : null}
         </div>
         {renderToastContentBasedOnVariant()}
         {showDismissButton || rightLink ? (
            rightLink ? (
               <Button
                  hierarchy={Hierarchy.Link}
                  subVariant={SubVariant.Gray}
                  size={'Small'}
                  className={cn(toastDescriptionStyles, textColor, 'ml-auto')}
                  onClick={() => rightLink.onClickLink()}
                  rightIcon={() => <>{rightLink.renderIcon}</>}
               >
                  {rightLink.label}
               </Button>
            ) : (
               <div
                  className={closeContainerStyles}
                  onClick={() => dismissToast && dismissToast()}
               >
                  <XCloseIcon height={20} width={20} fill={closeIconColor} />
               </div>
            )
         ) : null}
      </div>
   )
}

const ToastContainer = (props: ToastContainerProps) => (
   <Container
      position='bottom-center'
      autoClose={1000}
      hideProgressBar={true}
      newestOnTop={true}
      closeOnClick
      rtl={false}
      draggable
      pauseOnHover
      {...props}
   />
)
export { ToastComponent, ToastContainer }
